import { useState, useMemo } from 'react';
import {
  Combobox as HeadlessCombobox,
  ComboboxInput,
  ComboboxOptions,
  ComboboxOption as HeadlessComboboxOption,
  ComboboxButton
} from '@headlessui/react';
import { clsx } from 'clsx';

export type ComboboxOption = {
  label: string;
  value: number | string;
};

interface ComboboxProps {
  /** Required for accessibility */
  label: string;
  /** Visibility of the label */
  showLabel?: boolean;
  /** Placeholder text for the input */
  placeholder?: string;
  /** List of options for the combobox */
  options: ComboboxOption[];
  /** Immediately open the combobox options when the combobox input is focused */
  immediate?: boolean;
  /** Option text is text-sm */
  small?: boolean;
  /** Show or hide the search icon */
  showSearchIcon?: boolean;
  /** Show or hide the dropdown preview and icon */
  showDropdownIcon?: boolean;
  /** Maximum number of results to display */
  maxResults?: number;
  /** Enable or disable query debouncing (recommended when there are many options) */
  debounce?: boolean;
  /** Currently selected option */
  selectedOption: ComboboxOption | null;
  /** Callback when an option is selected */
  onChange: (option: ComboboxOption | null) => void;
}

function Combobox({
  label,
  options,
  immediate = true,
  selectedOption,
  onChange,
  placeholder = 'Search...',
  maxResults = 100,
  debounce = false,
  showSearchIcon = true,
  showDropdownIcon = false,
  showLabel = true,
  small = false
}: ComboboxProps) {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  useMemo(() => {
    if (!debounce) {
      setDebouncedQuery(query);
      return;
    }
    const handler = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(handler);
  }, [query, debounce]);

  const filteredOptions = useMemo(() => {
    const searchQuery = debounce ? debouncedQuery : query;
    if (!searchQuery) return options.slice(0, maxResults);
    return options
      .filter(option =>
        option.label.toLowerCase().includes(searchQuery.toLowerCase())
      )
      .slice(0, maxResults);
  }, [options, query, debouncedQuery, maxResults, debounce]);

  return (
    <HeadlessCombobox
      immediate={immediate}
      as="div"
      value={selectedOption}
      onChange={option => {
        onChange(option);
        setQuery('');
      }}
    >
      <label
        className={clsx(
          'text-sm font-medium text-gray-700',
          showLabel ? 'block' : 'sr-only'
        )}
      >
        {label}
      </label>
      <div className="relative">
        <ComboboxInput
          aria-label={label}
          placeholder={placeholder}
          className={clsx(
            'w-full font-sans border border-navy-300 rounded-lg p-2 text-text-default',
            small
              ? 'text-sm placeholder:text-sm'
              : 'text-base placeholder:text-base',
            showSearchIcon && showDropdownIcon ? 'pr-14' : 'pr-8',
            'placeholder:text-charcoal-500 data-[focus]:outline-0 data-[focus]:border-focus data-[focus]:ring-1 data-[focus]:ring-focus data-[open]:rounded-b-none'
          )}
          displayValue={(option: ComboboxOption) => option?.label}
          onChange={event => setQuery(event.target.value)}
        />
        <div className="absolute right-0 inset-y-0 flex items-center pr-2.5 space-x-2">
          {showSearchIcon && <i className="fa-solid fa-magnifying-glass" />}
          {showDropdownIcon && (
            <ComboboxButton
              as="button"
              className="text-charcoal-300 hover:text-charcoal-500"
            >
              <i className="fa-solid fa-chevron-down" />
            </ComboboxButton>
          )}
        </div>
      </div>

      <ComboboxOptions
        className={clsx(
          'w-[--input-width] text-text-default border-x border-b border-focus ring-1 ring-focus rounded-b-lg max-h-40 empty:invisible overflow-auto text-text-default',
          small && 'text-sm placeholder:text-sm'
        )}
        modal={false}
        anchor="bottom"
      >
        {filteredOptions.map((option, idx) => (
          <HeadlessComboboxOption
            key={option.value}
            value={option}
            className={clsx(
              'font-sans p-2 bg-white data-[focus]:bg-charcoal-25',
              idx === filteredOptions.length - 1 && 'rounded-b-lg'
            )}
          >
            {option.label}
          </HeadlessComboboxOption>
        ))}
      </ComboboxOptions>
    </HeadlessCombobox>
  );
}

export { Combobox };
